import { useState } from 'react';
import {
  MoreHorizontal,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Undo2,
  RefreshCw,
  Copy,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { tradeService } from '../services/trade-service';
import {
  nextOutcomes,
  OUTCOME_ACTION,
  LOST_REASONS,
  LOST_REASON_LABEL,
  type QuoteOutcome,
  type QuoteLostReason,
} from '../utils/quote-outcome';

/**
 * Lo que se puede hacer con una cotización guardada.
 *
 * Todo junto en un menú y no en botones sueltos: son seis acciones y solo dos
 * se usan seguido. Seis botones por fila convierten la tabla en un tablero de
 * mandos donde no se distingue lo importante.
 *
 * El menú **solo ofrece las transiciones válidas** (`nextOutcomes`). Mostrar
 * "El cliente aceptó" sobre un borrador que nunca se mandó es ofrecer un botón
 * que el servidor va a rechazar, y el usuario aprende a desconfiar del menú.
 */

interface QuoteActionsProps {
  quoteId: string;
  quoteNumber: string;
  outcome: QuoteOutcome;
  /** Se dispara cuando algo cambió y hay que releer la lista. */
  onChanged: () => void;
  onRequote: () => void;
  onDuplicate: () => void;
}

const ICONS: Record<QuoteOutcome, typeof Send> = {
  DRAFT: Undo2,
  SENT: Send,
  ACCEPTED: CheckCircle2,
  LOST: XCircle,
  NO_RESPONSE: Clock,
};

export function QuoteActions({
  quoteId,
  quoteNumber,
  outcome,
  onChanged,
  onRequote,
  onDuplicate,
}: QuoteActionsProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [reason, setReason] = useState<QuoteLostReason>('PRICE');
  const [notes, setNotes] = useState('');

  const apply = async (
    to: QuoteOutcome,
    extra?: { lostReason: QuoteLostReason; lostNotes: string | null }
  ) => {
    setSaving(true);
    try {
      const r = await tradeService.setQuoteOutcome(quoteId, {
        outcome: to,
        lostReason: extra?.lostReason ?? null,
        lostNotes: extra?.lostNotes ?? null,
      });

      toast({
        title: `${quoteNumber} · ${OUTCOME_ACTION[to]}`,
        // Registrar la aceptación y convertirla en orden son dos decisiones
        // distintas; decirlo evita que alguien crea que ya generó la orden.
        description: r.needsConversion
          ? 'Queda registrada como aceptada. Convertila en orden cuando tengas los datos del embarque.'
          : undefined,
        variant: 'success',
      });

      setLostOpen(false);
      setNotes('');
      onChanged();
    } catch (err) {
      toast({
        title: 'No se pudo registrar',
        description:
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Reintentá en un momento.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const disponibles = nextOutcomes(outcome);
  // "Otro" sin explicación no le dice nada a quien lea el reporte en seis meses.
  const faltaNota = reason === 'OTHER' && notes.trim() === '';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label={`Acciones de ${quoteNumber}`}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MoreHorizontal className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Qué pasó con el cliente
          </DropdownMenuLabel>

          {disponibles.map((to) => {
            const Icon = ICONS[to];
            return (
              <DropdownMenuItem
                key={to}
                onSelect={(e) => {
                  // Perder pide motivo: se abre el diálogo en vez de guardar.
                  if (to === 'LOST') {
                    e.preventDefault();
                    setLostOpen(true);
                    return;
                  }
                  void apply(to);
                }}
              >
                <Icon className="mr-2 h-3.5 w-3.5" />
                {OUTCOME_ACTION[to]}
              </DropdownMenuItem>
            );
          })}

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Volver a cotizar
          </DropdownMenuLabel>

          <DropdownMenuItem onSelect={onRequote}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Recotizar con precios de hoy
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onDuplicate}>
            <Copy className="mr-2 h-3.5 w-3.5" />
            Duplicar para otra ruta
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ── Por qué se perdió ── */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Por qué se perdió {quoteNumber}?</DialogTitle>
            <DialogDescription>
              El motivo es lo único que después permite ver un patrón. Una lista de cotizaciones
              perdidas sin motivo no se puede leer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            {LOST_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  reason === r
                    ? 'border-primary bg-primary/5 font-medium'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <span
                  className={cn(
                    'h-3.5 w-3.5 shrink-0 rounded-full border-2',
                    reason === r ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                  )}
                  aria-hidden
                />
                {LOST_REASON_LABEL[r]}
              </button>
            ))}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="lost-notes" className="text-xs font-medium">
              {reason === 'OTHER' ? 'Qué pasó (obligatorio)' : 'Detalle (opcional)'}
            </label>
            <Textarea
              id="lost-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={
                reason === 'PRICE'
                  ? 'Ej.: nos ganaron por 400 USD'
                  : 'Lo que sirva para entenderlo en seis meses'
              }
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setLostOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={saving || faltaNota}
              onClick={() =>
                void apply('LOST', { lostReason: reason, lostNotes: notes.trim() || null })
              }
            >
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Registrar la pérdida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
